import type { PropsWithChildren } from 'react';

import { Image } from '@change/ds-core-ui/Image';
import { Text } from '@change/ds-core-ui/Text';
import { ErrorMessage } from '@change/ds-forms';
import { Grid } from '@change/ds-layout';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';

import { ImageGenerationLoading } from '#src/app/pages/startAPetitionV2/shared/components';

const INCOMPLETE_STATE = {
	idle: null,
	pending: <ImageGenerationLoading />,
	error: (
		<ErrorMessage className="[&>span:last-child]:text-(--color-stroke-error-weak)">
			<Translate value="corgi.start_a_petition.preview.image.error" />
		</ErrorMessage>
	),
} as const;

type HeroProps = PropsWithChildren<{ status: 'idle' | 'pending' | 'error' | 'success'; imageSrc?: string }>;
export const Hero: React.FC<HeroProps> = ({ children, status, imageSrc }) => {
	// no image
	if (imageSrc === undefined && status !== 'success') {
		return (
			<div
				className={cn(
					// text-typography-inverse-strong-fixed isn't for text here — it gives the shimmer's
					// currentcolor gradient the correct color; the inner text/icon wrapper sets the same
					// class again for its own content, which is harmless duplication.
					// eslint-disable-next-line @change-ds/ds-no-raw-color-classname -- no-image hero placeholder, black in both modes under -fixed text (no fixed fill token)
					'text-typography-inverse-strong-fixed grid min-h-[280px] grid-rows-[1fr_min-content] gap-4 bg-black px-4 pt-11 pb-4 md:px-0',
					status === 'pending' && 'hero-shimmer',
				)}
				data-qa="hero-no-image"
			>
				<Grid className="pr-0 pl-0">
					<div className="col-span-full grid grid-rows-[1fr_min-content] gap-y-8 md:col-span-6 md:col-start-2 lg:col-span-8 lg:col-start-3">
						<div className="flex gap-2 justify-self-center">{INCOMPLETE_STATE[status]}</div>
						<div className="bg-fill-brand-strong mt-auto h-[6px] w-[52px]"></div>
					</div>
				</Grid>

				<Grid className="justify-center pr-0 pl-0">
					<Text
						variant="heading"
						size="x-large"
						className="text-typography-inverse-strong-fixed col-span-full md:col-span-6 md:col-start-2 lg:col-span-8 lg:col-start-3"
					>
						{children}
					</Text>
				</Grid>
			</div>
		);
	}

	// with image
	return (
		<div
			className="bg-fill-strong grid min-h-[280px] grid-cols-[auto] grid-rows-[minmax(100px,1fr)_50px_repeat(4,auto)]"
			data-qa="hero-with-image"
		>
			<div className="col-span-full row-[1/3] overflow-hidden">
				<Image className="h-full w-full max-w-full object-cover" src={imageSrc} alt="" />
			</div>

			<Grid className="relative col-span-full row-[2/3] pr-0 pl-0">
				<div className="bg-fill-brand-strong z-10 col-span-full mt-auto mb-2.5 ml-4 h-[6px] w-[52px] md:col-span-6 md:col-start-2 md:ml-0 lg:col-span-8 lg:col-start-3"></div>
				{/* eslint-disable-next-line @change-ds/ds-no-raw-color-classname -- photo scrim, black in both modes (no fixed scrim token) */}
				<div className="absolute h-full w-full bg-linear-to-t from-black via-black via-0% to-transparent to-100%"></div>
			</Grid>

			{/* eslint-disable-next-line @change-ds/ds-no-raw-color-classname -- hero text area continues the photo scrim, black in both modes (no fixed fill token) */}
			<Grid className="z-10 col-[1/1] row-[3/-1] justify-center bg-black pr-0 pl-0">
				<Text
					variant="heading"
					size="x-large"
					className="text-typography-inverse-strong-fixed col-span-full px-4 pb-4 md:col-span-6 md:col-start-2 md:px-0 lg:col-span-8 lg:col-start-3"
				>
					{children}
				</Text>
			</Grid>
		</div>
	);
};
